import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const DashboardEntreprise = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [declarations, setDeclarations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDeclarationModal, setShowDeclarationModal] = useState(false);
  const [editingDeclaration, setEditingDeclaration] = useState<any>(null);
  const [declarationData, setDeclarationData] = useState({
    year: new Date().getFullYear(),
    permanent_employees: 0,
    temporary_employees: 0,
    male_employees: 0,
    female_employees: 0,
    cdi_contracts: 0,
    cdd_contracts: 0,
    total_payroll: 0,
    average_salary: 0,
    min_salary: 0,
    max_salary: 0,
    training_hours: 0,
    training_participants: 0,
    training_budget: 0,
    work_accidents: 0,
    sick_leave_days: 0,
    safety_training_hours: 0,
    union_members: 0,
    collective_agreements: 0,
    social_conflicts: 0
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  useEffect(() => {
    fetchDeclarations();
  }, []);

  const fetchDeclarations = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/declarations');
      if (response.data.success) {
        setDeclarations(response.data.declarations || []);
      }
    } catch (error) {
      console.error('Error fetching declarations:', error);
    }
    setLoading(false);
  };

  const handleCreateDeclaration = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await api.post('/api/declarations', declarationData);
      if (response.data.success) {
        alert('Déclaration créée avec succès!');
        setShowDeclarationModal(false);
        fetchDeclarations();
        resetForm();
      }
    } catch (error: any) {
      alert('Erreur: ' + (error.response?.data?.error?.message || 'Échec de création'));
    }
  };

  const handleUpdateDeclaration = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingDeclaration) return;
    try {
      const response = await api.put(`/api/declarations/${editingDeclaration.id}`, declarationData);
      if (response.data.success) {
        alert('Déclaration mise à jour avec succès!');
        setShowDeclarationModal(false);
        setEditingDeclaration(null);
        fetchDeclarations();
        resetForm();
      }
    } catch (error: any) {
      alert('Erreur: ' + (error.response?.data?.error?.message || 'Échec de mise à jour'));
    }
  };

  const handleSubmitDeclaration = async (id: number) => {
    if (!confirm('Soumettre cette déclaration pour validation?')) return;
    try {
      const response = await api.post(`/api/declarations/${id}/submit`);
      if (response.data.success) {
        alert('Déclaration soumise avec succès!');
        fetchDeclarations();
      }
    } catch (error: any) {
      alert('Erreur: ' + (error.response?.data?.error?.message || 'Échec'));
    }
  };

  const openEditModal = (declaration: any) => {
    setEditingDeclaration(declaration);
    setDeclarationData({
      year: declaration.year,
      permanent_employees: declaration.permanent_employees || 0,
      temporary_employees: declaration.temporary_employees || 0,
      male_employees: declaration.male_employees || 0,
      female_employees: declaration.female_employees || 0,
      cdi_contracts: declaration.cdi_contracts || 0,
      cdd_contracts: declaration.cdd_contracts || 0,
      total_payroll: declaration.total_payroll || 0,
      average_salary: declaration.average_salary || 0,
      min_salary: declaration.min_salary || 0,
      max_salary: declaration.max_salary || 0,
      training_hours: declaration.training_hours || 0,
      training_participants: declaration.training_participants || 0,
      training_budget: declaration.training_budget || 0,
      work_accidents: declaration.work_accidents || 0,
      sick_leave_days: declaration.sick_leave_days || 0,
      safety_training_hours: declaration.safety_training_hours || 0,
      union_members: declaration.union_members || 0,
      collective_agreements: declaration.collective_agreements || 0,
      social_conflicts: declaration.social_conflicts || 0
    });
    setShowDeclarationModal(true);
  };

  const resetForm = () => {
    setDeclarationData({
      year: new Date().getFullYear(),
      permanent_employees: 0,
      temporary_employees: 0,
      male_employees: 0,
      female_employees: 0,
      cdi_contracts: 0,
      cdd_contracts: 0,
      total_payroll: 0,
      average_salary: 0,
      min_salary: 0,
      max_salary: 0,
      training_hours: 0,
      training_participants: 0,
      training_budget: 0,
      work_accidents: 0,
      sick_leave_days: 0,
      safety_training_hours: 0,
      union_members: 0,
      collective_agreements: 0,
      social_conflicts: 0
    });
  };

  const canEdit = (declaration: any) => {
    return declaration.status === 'draft' && (user?.role === 'admin' || user?.role === 'contributor');
  };

  const canSubmit = (declaration: any) => {
    return declaration.status === 'draft' && (user?.role === 'admin' || user?.role === 'contributor');
  };

  const pendingDeclarations = declarations.filter((d: any) => d.status === 'submitted' || d.status === 'pending');
  const validatedDeclarations = declarations.filter((d: any) => d.status === 'validated');
  const draftDeclarations = declarations.filter((d: any) => d.status === 'draft');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Bilan Social - Mon Entreprise</h1>
            <p className="text-sm text-gray-600">{user?.role === 'admin' ? 'Administrateur' : user?.role === 'contributor' ? 'Contributeur' : 'Lecteur'}</p>
          </div>
          <button onClick={handleLogout} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
            Déconnexion
          </button>
        </div>
      </nav>

      {/* Welcome Section */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-2">Bienvenue, {user?.first_name} {user?.last_name}</h2>
          <p className="text-gray-600">Email : {user?.email}</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Total</h3>
            <p className="text-3xl font-bold text-blue-600">{declarations.length}</p>
            <p className="text-sm text-gray-600 mt-2">Déclarations</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Brouillons</h3>
            <p className="text-3xl font-bold text-gray-600">{draftDeclarations.length}</p>
            <p className="text-sm text-gray-600 mt-2">En cours</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">En attente</h3>
            <p className="text-3xl font-bold text-orange-600">{pendingDeclarations.length}</p>
            <p className="text-sm text-gray-600 mt-2">Validation</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Validées</h3>
            <p className="text-3xl font-bold text-green-600">{validatedDeclarations.length}</p>
            <p className="text-sm text-gray-600 mt-2">Approuvées</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b">
            <div className="flex space-x-4 px-6">
              <button
                onClick={() => setActiveTab('overview')}
                className={`py-4 px-4 font-medium border-b-2 transition-colors ${
                  activeTab === 'overview' 
                    ? 'border-blue-600 text-blue-600' 
                    : 'border-transparent text-gray-600 hover:text-gray-800'
                }`}
              >
                Vue d'ensemble
              </button>
              <button
                onClick={() => setActiveTab('declarations')}
                className={`py-4 px-4 font-medium border-b-2 transition-colors ${
                  activeTab === 'declarations' 
                    ? 'border-blue-600 text-blue-600' 
                    : 'border-transparent text-gray-600 hover:text-gray-800'
                }`}
              >
                Mes Déclarations
              </button>
            </div>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-gray-600">Chargement...</p>
              </div>
            ) : (
              <>
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div>
                    <h3 className="text-xl font-semibold mb-4">Tableau de bord</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="border rounded-lg p-4">
                        <h4 className="font-semibold mb-3 flex items-center">
                          <span className="text-orange-600 mr-2">⏳</span>
                          Déclarations en attente
                        </h4>
                        {pendingDeclarations.length === 0 ? (
                          <p className="text-gray-600 text-sm">Aucune déclaration en attente</p>
                        ) : (
                          pendingDeclarations.map((decl: any) => (
                            <div key={decl.id} className="py-2 border-b last:border-b-0">
                              <p className="font-medium">Année {decl.year}</p>
                              <p className="text-sm text-gray-600">Statut: {decl.status}</p>
                            </div>
                          ))
                        )}
                      </div>
                      <div className="border rounded-lg p-4">
                        <h4 className="font-semibold mb-3 flex items-center">
                          <span className="text-green-600 mr-2">✅</span>
                          Déclarations validées
                        </h4>
                        {validatedDeclarations.length === 0 ? (
                          <p className="text-gray-600 text-sm">Aucune déclaration validée</p>
                        ) : (
                          validatedDeclarations.map((decl: any) => (
                            <div key={decl.id} className="py-2 border-b last:border-b-0">
                              <p className="font-medium">Année {decl.year}</p>
                              <p className="text-sm text-gray-600">
                                Validée le {decl.validated_at ? new Date(decl.validated_at).toLocaleDateString() : 'N/A'}
                              </p>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {user?.role !== 'reader' && (
                      <div className="mt-6">
                        <button
                          onClick={() => { resetForm(); setShowDeclarationModal(true); }}
                          className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                        >
                          + Nouvelle Déclaration
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Declarations Tab */}
                {activeTab === 'declarations' && (
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-xl font-semibold">Mes Déclarations du Bilan Social</h3>
                      {user?.role !== 'reader' && (
                        <button
                          onClick={() => { resetForm(); setShowDeclarationModal(true); }}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                        >
                          + Nouvelle Déclaration
                        </button>
                      )}
                    </div>

                    {declarations.length === 0 ? (
                      <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <p className="text-gray-600 mb-4">Aucune déclaration pour le moment</p>
                        {user?.role !== 'reader' && (
                          <button
                            onClick={() => { resetForm(); setShowDeclarationModal(true); }}
                            className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700"
                          >
                            Créer ma première déclaration
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Année</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Effectif</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date création</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {declarations.map((decl: any) => (
                              <tr key={decl.id}>
                                <td className="px-6 py-4 whitespace-nowrap font-medium">{decl.year}</td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  {(decl.permanent_employees || 0) + (decl.temporary_employees || 0)} employés
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`px-2 py-1 text-xs rounded-full ${
                                    decl.status === 'validated' ? 'bg-green-100 text-green-800' :
                                    decl.status === 'submitted' ? 'bg-orange-100 text-orange-800' :
                                    decl.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {decl.status === 'validated' ? 'Validée' :
                                     decl.status === 'submitted' ? 'En attente' :
                                     decl.status === 'rejected' ? 'Rejetée' :
                                     'Brouillon'}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  {new Date(decl.created_at).toLocaleDateString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex space-x-2">
                                    {canEdit(decl) && (
                                      <button
                                        onClick={() => openEditModal(decl)}
                                        className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                                      >
                                        ✏️ Modifier
                                      </button>
                                    )}
                                    {canSubmit(decl) && (
                                      <button
                                        onClick={() => handleSubmitDeclaration(decl.id)}
                                        className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                                      >
                                        ✓ Soumettre
                                      </button>
                                    )}
                                    {decl.rejection_reason && (
                                      <button
                                        onClick={() => alert(`Raison du rejet: ${decl.rejection_reason}`)}
                                        className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                                      >
                                        ℹ️ Voir raison
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center py-6 text-gray-600">
          <p className="text-sm">
            Made by <a href="https://sayele.co" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 font-medium">SAYELE</a>
          </p>
        </div>
      </div>

      {/* Declaration Modal */}
      {showDeclarationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 my-8 max-h-screen overflow-y-auto">
            <h3 className="text-xl font-semibold mb-4">
              {editingDeclaration ? 'Modifier la Déclaration' : 'Nouvelle Déclaration du Bilan Social'}
            </h3>
            <form onSubmit={editingDeclaration ? handleUpdateDeclaration : handleCreateDeclaration}>
              {/* Section 1: Effectifs et Contrats */}
              <div className="mb-6">
                <h4 className="font-semibold text-lg mb-3 text-blue-600 border-b pb-2">1. Effectifs et Contrats</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Année *</label>
                    <input
                      type="number"
                      required
                      min="2020"
                      max="2030"
                      value={declarationData.year}
                      onChange={(e) => setDeclarationData({ ...declarationData, year: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Employés permanents</label>
                    <input
                      type="number"
                      min="0"
                      value={declarationData.permanent_employees}
                      onChange={(e) => setDeclarationData({ ...declarationData, permanent_employees: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Employés temporaires</label>
                    <input
                      type="number"
                      min="0"
                      value={declarationData.temporary_employees}
                      onChange={(e) => setDeclarationData({ ...declarationData, temporary_employees: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Employés masculins</label>
                    <input
                      type="number"
                      min="0"
                      value={declarationData.male_employees}
                      onChange={(e) => setDeclarationData({ ...declarationData, male_employees: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Employés féminins</label>
                    <input
                      type="number"
                      min="0"
                      value={declarationData.female_employees}
                      onChange={(e) => setDeclarationData({ ...declarationData, female_employees: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contrats CDI</label>
                    <input
                      type="number"
                      min="0"
                      value={declarationData.cdi_contracts}
                      onChange={(e) => setDeclarationData({ ...declarationData, cdi_contracts: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contrats CDD</label>
                    <input
                      type="number"
                      min="0"
                      value={declarationData.cdd_contracts}
                      onChange={(e) => setDeclarationData({ ...declarationData, cdd_contracts: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    />
                  </div>
                </div>
              </div>

              {/* Section 2: Rémunérations */}
              <div className="mb-6">
                <h4 className="font-semibold text-lg mb-3 text-green-600 border-b pb-2">2. Rémunérations (GNF)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Masse salariale totale</label>
                    <input
                      type="number"
                      min="0"
                      value={declarationData.total_payroll}
                      onChange={(e) => setDeclarationData({ ...declarationData, total_payroll: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Salaire moyen</label>
                    <input
                      type="number"
                      min="0"
                      value={declarationData.average_salary}
                      onChange={(e) => setDeclarationData({ ...declarationData, average_salary: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Salaire minimum</label>
                    <input
                      type="number"
                      min="0"
                      value={declarationData.min_salary}
                      onChange={(e) => setDeclarationData({ ...declarationData, min_salary: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Salaire maximum</label>
                    <input
                      type="number"
                      min="0"
                      value={declarationData.max_salary}
                      onChange={(e) => setDeclarationData({ ...declarationData, max_salary: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    />
                  </div>
                </div>
              </div>

              {/* Section 3: Formation */}
              <div className="mb-6">
                <h4 className="font-semibold text-lg mb-3 text-purple-600 border-b pb-2">3. Formation Professionnelle</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Heures de formation</label>
                    <input
                      type="number"
                      min="0"
                      value={declarationData.training_hours}
                      onChange={(e) => setDeclarationData({ ...declarationData, training_hours: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Employés formés</label>
                    <input
                      type="number"
                      min="0"
                      value={declarationData.training_participants}
                      onChange={(e) => setDeclarationData({ ...declarationData, training_participants: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Budget formation (GNF)</label>
                    <input
                      type="number"
                      min="0"
                      value={declarationData.training_budget}
                      onChange={(e) => setDeclarationData({ ...declarationData, training_budget: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    />
                  </div>
                </div>
              </div>

              {/* Section 4: Santé et Sécurité */}
              <div className="mb-6">
                <h4 className="font-semibold text-lg mb-3 text-red-600 border-b pb-2">4. Santé et Sécurité</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Accidents de travail</label>
                    <input
                      type="number"
                      min="0"
                      value={declarationData.work_accidents}
                      onChange={(e) => setDeclarationData({ ...declarationData, work_accidents: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Jours d'arrêt maladie</label>
                    <input
                      type="number"
                      min="0"
                      value={declarationData.sick_leave_days}
                      onChange={(e) => setDeclarationData({ ...declarationData, sick_leave_days: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Heures formation sécurité</label>
                    <input
                      type="number"
                      min="0"
                      value={declarationData.safety_training_hours}
                      onChange={(e) => setDeclarationData({ ...declarationData, safety_training_hours: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    />
                  </div>
                </div>
              </div>

              {/* Section 5: Relations Sociales */}
              <div className="mb-6">
                <h4 className="font-semibold text-lg mb-3 text-indigo-600 border-b pb-2">5. Relations Sociales</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Membres syndicaux</label>
                    <input
                      type="number"
                      min="0"
                      value={declarationData.union_members}
                      onChange={(e) => setDeclarationData({ ...declarationData, union_members: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Accords collectifs</label>
                    <input
                      type="number"
                      min="0"
                      value={declarationData.collective_agreements}
                      onChange={(e) => setDeclarationData({ ...declarationData, collective_agreements: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Conflits sociaux</label>
                    <input
                      type="number"
                      min="0"
                      value={declarationData.social_conflicts}
                      onChange={(e) => setDeclarationData({ ...declarationData, social_conflicts: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6 pt-6 border-t">
                <button
                  type="button"
                  onClick={() => { setShowDeclarationModal(false); setEditingDeclaration(null); resetForm(); }}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingDeclaration ? 'Mettre à jour' : 'Créer la déclaration'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardEntreprise;
